import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { acme } from '../../theme/acmeTheme';
import { Card, CardContent } from './Card';
import { DashboardMetricIcon } from './dashboardMetricIcons';

const stats = [
  { key: 'campaigns', label: 'Campaigns', value: '12', icon: 'campaigns' },
  { key: 'recipients', label: 'Recipients', value: '8,420', rate: '98%', icon: 'recipients' },
  { key: 'opens', label: 'Opens', value: '3,612', rate: '43%', icon: 'opens' },
  { key: 'clicks', label: 'Clicks', value: '891', rate: '11%', icon: 'clicks' },
  { key: 'unsub', label: 'Unsubscribed', value: '24', rate: '0.3%', icon: 'unsubscribed' },
];

/**
 * Static preview of the Mailsball /dashboard UI for marketing hero.
 */
export default function DashboardPreview({ compact = false }) {
  const iconSize = compact ? 18 : 22;

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: acme.radius.lg,
        border: `1px solid ${acme.colors.border}`,
        boxShadow: acme.shadow.xl,
        backgroundColor: acme.colors.background,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${acme.colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: acme.colors.muted,
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {['#FF5F57', '#FFBD2E', '#28CA41'].map((c) => (
            <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
          ))}
        </Box>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: acme.colors.mutedForegroundSolid }}>
          Dashboard · Mailsball
        </Typography>
        <Box sx={{ width: 40 }} />
      </Box>

      <Box sx={{ p: compact ? 1.5 : 2, backgroundColor: acme.colors.pageBg }}>
        <Box sx={{ textAlign: 'center', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: compact ? '0.9rem' : '1rem', color: acme.colors.foreground }}>
            Hi, User! Welcome back!
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: acme.colors.mutedForegroundSolid }}>
            Your monitoring dashboard
          </Typography>
        </Box>

        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={6}>
            <Card sx={{ boxShadow: 'none' }}>
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  <DashboardMetricIcon name="plan" size={16} tone="default" />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Plan: Pro</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.65rem', color: acme.colors.mutedForegroundSolid }}>
                  Emails used: 2,450 / 10,000
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card sx={{ boxShadow: 'none' }}>
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  <DashboardMetricIcon name="sms" size={16} tone="default" />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>SMS Marketing</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.65rem', color: acme.colors.mutedForegroundSolid }}>
                  Credits: 1,200 · Sent: 340
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 2, borderBottom: `1px solid ${acme.colors.border}`, mb: 1.5, px: 0.5 }}>
          {['Emails', 'SMS', 'Notifications'].map((tab, i) => (
            <Typography
              key={tab}
              sx={{
                fontSize: '0.7rem',
                fontWeight: i === 0 ? 600 : 400,
                color: i === 0 ? acme.colors.foreground : acme.colors.mutedForegroundSolid,
                pb: 0.75,
                borderBottom: i === 0 ? `2px solid ${acme.colors.foreground}` : '2px solid transparent',
              }}
            >
              {tab}
            </Typography>
          ))}
        </Box>

        <Grid container spacing={1}>
          {stats.map((s) => (
            <Grid item xs={4} sm={2.4} key={s.key}>
              <Card
                sx={{
                  boxShadow: 'none',
                  height: '100%',
                  transition: 'transform 0.15s',
                  '&:hover': { transform: 'translateY(-2px)' },
                }}
              >
                <CardContent sx={{ p: 1, textAlign: 'center', '&:last-child': { pb: 1 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                    <DashboardMetricIcon name={s.icon} size={iconSize} tone="default" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.55rem',
                      fontWeight: 700,
                      color: acme.colors.mutedForegroundSolid,
                      letterSpacing: '0.04em',
                    }}
                  >
                    {s.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: compact ? '0.85rem' : '1rem',
                      fontWeight: 700,
                      color: acme.colors.foreground,
                      lineHeight: 1.2,
                    }}
                  >
                    {s.value}
                  </Typography>
                  {s.rate && (
                    <Typography sx={{ fontSize: '0.55rem', color: acme.colors.mutedForegroundSolid, mt: 0.25 }}>
                      {s.rate} rate
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card sx={{ boxShadow: 'none', mt: 1.5 }}>
          <CardContent sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>18</Typography>
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: acme.colors.iconMuted }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>25</Typography>
            <Typography sx={{ fontSize: '0.65rem', color: acme.colors.mutedForegroundSolid, ml: 1 }}>
              Jun 2026 · Last 7 days
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
